import React, { useEffect, useState } from "react";
import { Field, useFormikContext } from "formik";

import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import { getChangeOfParticularsData } from "config/changesDocumentData";
import { useDispatch, useSelector } from "react-redux";
import OnlyOfficeDocumentEditor from "components/lib/Shared/OnlyOfficeDocumentEditor";
import { getUploadChangeDocumentsAsync } from "state/slices/company";
import { getFullName } from "utils";
import { Constants } from "config/constants";
import { format } from "date-fns";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";

const DocumentsUpload = ({ field, form, resolvedFields = [], ...props }) => {
  const { changedFields } = props;
  const { setFieldValue } = useFormikContext();
  const dispatch = useDispatch();
  const [documentName, setDocumentName] = useState("");
  const { company } = useSelector((state) => state.companyDashboardSlice);
  const currentUser = useSelector((state) => state.userSlice);

  const handleSetValue = (e) => {
    const selectedValue = e.target.value;
    // MODIFIED: Clear other fields when changing modeType
    setFieldValue(field.name, {
      modeType: selectedValue,
      subModeType: undefined,
      statutoryDeclaration: null,
      getStatutoryDeclarationName: undefined,
    });
  };

  const getDocumentData = async (documentType) => {
    const response = await dispatch(
      getUploadChangeDocumentsAsync({
        companyId: company._id,
        data: {
          officialId: form.values._id,
          fileType: documentType,
          idType: form.values.idType,
          designation: form.values.designation,
          changeOfParticularDate: form.values.cessationDate,
          identificationDetails: form.values.identificationDetails,
          names: form.values.names,
          residentialAddressDetails: form.values.residentialAddressDetails,
          electronicAddress: form.values.electronicAddress,
          postalAddress: form.values.postalAddress,
          changedFields,
        },
      })
    );
    return response?.payload?.fileName;
  };

  useEffect(() => {
    if (
      form.values.statutoryDeclaration?.subModeType ===
        "platform-resolution-type" &&
      !form.values.statutoryDeclaration?.getStatutoryDeclarationName
    ) {
      getDocumentData(
        Constants.Template_FILE_TYPES.ADDRESS_STATUTORY_DECLARATION
      ).then((fileName) => {
        // MODIFIED: Set filename in statutoryDeclaration.getStatutoryDeclarationName
        form.setFieldValue(
          "statutoryDeclaration.getStatutoryDeclarationName",
          fileName
        );
        setDocumentName(fileName);
      });
    }
  }, [form.values.statutoryDeclaration]);

  return (
    <div className="mt-4 flex flex-col">
      <DocumentSection
        form={form}
        documentName={documentName}
        handleSetValue={handleSetValue}
        currentUser={currentUser}
        company={company}
        sectionName={field.name}
        title={field.name}
        changedFields={changedFields}
      />
    </div>
  );
};

const DocumentSection = ({
  form,
  documentName,
  handleSetValue,
  currentUser,
  company,
  sectionName,
  title,
  changedFields,
}) => (
  <div className="flex flex-col">
    <span>
      {title === "changeDocumentsType"
        ? "Change Documents Type"
        : title === "statutoryDeclaration"
        ? "Statutory Declaration"
        : title}
      :
    </span>
    <div className="grid grid-flow-col justify-stretch mt-6">
      <RadioOption
        form={form}
        sectionName={sectionName}
        handleSetValue={handleSetValue}
        optionValue="pre-signed-documents"
        label="I have signed documents"
      />
      {title === "statutoryDeclaration" && changedFields?.electronicAddress && (
        <RadioOption
          form={form}
          sectionName={sectionName}
          handleSetValue={handleSetValue}
          optionValue="not-signed-documents"
          label="Not yet signed"
        />
      )}
    </div>

    {/* Conditional Rendering */}
    {form.values[sectionName]?.modeType === "pre-signed-documents" && (
      <FileUploadField
        sectionName={`${sectionName}`}
        idType={form.values.idType}
      />
    )}
    {form.values[sectionName]?.modeType === "not-signed-documents" && (
      <PlatformResolutionSection
        documentName={documentName}
        form={form}
        currentUser={currentUser}
        company={company}
        sectionName={sectionName}
      />
    )}
  </div>
);

const RadioOption = ({
  form,
  sectionName,
  handleSetValue,
  optionValue,
  label,
}) => (
  <div className="flex items-center">
    <label
      htmlFor={`${sectionName}-${optionValue}`}
      className="flex items-center cursor-pointer"
    >
      <Field
        id={`${sectionName}-${optionValue}`}
        name={`${sectionName}.modeType`}
        type="radio"
        value={optionValue}
        checked={form.values[sectionName]?.modeType === optionValue}
        onChange={handleSetValue}
        className="cursor-pointer form-radio h-4 w-4 border-green-900 border-2 focus:ring-green-50"
      />
      <span className="ml-2">{label}</span>
    </label>
  </div>
);

const FileUploadField = ({ sectionName, idType }) => {
  const documentData = (sectionName) => {
    if (sectionName === "changeDocumentsType") {
      return idType === Constants.ID_TYPES.FOREIGN_COMPANY ||
        idType === Constants.ID_TYPES.LOCAL_COMPANY ||
        idType === Constants.ID_TYPES.OTHER_PARASTATALS
        ? "nameChangeCorporate"
        : "nameChangeIndividual";
    }
    if (sectionName === "statutoryDeclaration") {
      return "contactChangeIndividual";
    }
  };
  const documents = getChangeOfParticularsData(documentData(sectionName));
  return (
    <div className="container bg-white mt-6 px-4 py-4 shadow-sm rounded-md">
      <Field
        // MODIFIED: Target statutoryDeclaration.statutoryDeclaration for files
        name={`${sectionName}`}
        component={FileUploadForm}
        documentData={documents}
        disabled={false}
      />
    </div>
  );
};

const PlatformResolutionSection = ({
  documentName,
  form,
  currentUser,
  company,
  sectionName,
}) => {
  const handleSetValue = (e) => {
    const selectedValue = e.target.value;
    // MODIFIED: Clear irrelevant fields when changing subModeType
    form.setFieldValue(sectionName, {
      ...form.values[sectionName],
      subModeType: selectedValue,
      statutoryDeclaration:
        selectedValue === "self-resolution-type" ? null : null, // Always clear file
      getStatutoryDeclarationName:
        selectedValue === "platform-resolution-type"
          ? form.values[sectionName].getStatutoryDeclarationName
          : undefined,
    });
  };

  return (
    <div className="mt-6 bg-white px-4 py-6 shadow-sm rounded-md">
      <h3 className="mb-2">Statutory Declaration</h3>
      <div className="bg-[#EDF0F5] flex flex-col justify-evenly mb-4 px-4 py-4 shadow-sm rounded-md space-y-3">
        <div className="flex items-center">
          <label
            htmlFor="ownStatutoryDeclaration"
            className="flex items-center cursor-pointer"
          >
            <Field
              id="ownStatutoryDeclaration"
              name={`${sectionName}.subModeType`}
              type="radio"
              value="self-resolution-type"
              checked={
                form.values[sectionName]?.subModeType === "self-resolution-type"
              }
              onChange={handleSetValue}
              className="cursor-pointer form-radio h-4 w-4 border-green-900 border-2 focus:ring-green-50"
            />
            <span className="ml-2">Upload your own Statutory Declaration</span>
          </label>
        </div>
        {form.values[sectionName]?.subModeType === "self-resolution-type" && (
          <FileUploadField sectionName={`${sectionName}`} />
        )}
        <div className="flex items-center">
          <label
            htmlFor="platformResolutionType"
            className="flex items-center cursor-pointer"
          >
            <Field
              id="platformResolutionType"
              name={`${sectionName}.subModeType`}
              type="radio"
              value="platform-resolution-type"
              checked={
                form.values[sectionName]?.subModeType ===
                "platform-resolution-type"
              }
              onChange={handleSetValue}
              className="cursor-pointer form-radio h-4 w-4 border-green-900 border-2 focus:ring-green-50"
            />
            <span className="ml-2">
              Adopting our Statutory Declaration Template
            </span>
          </label>
        </div>
        {form.values[sectionName]?.subModeType ===
          "platform-resolution-type" && (
          <div className="px-4 py-4 mt-2 mb-4">
            {documentName ? (
              <OnlyOfficeDocumentEditor
                url={`https://sfl-bucket.s3.us-west-2.amazonaws.com/images/${documentName}`}
                editorKey={`${currentUser._id}-${company._id}-${documentName}`}
                userFullName={getFullName(currentUser.names)}
                userId={currentUser._id}
                companyId={company._id}
                documentType="word"
                fileType="docx"
                mode="edit"
                title={`${format(
                  new Date(),
                  "dd/MM/yyyy"
                )} - Director Resignation Letter - ${getFullName(
                  currentUser.names
                )}`}
                containerHeight="600px"
                permissions={{ edit: true }}
              />
            ) : (
              <p className="flex justify-center py-4">
                <SpinnerIcon className="text-gray-500" />
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsUpload;
